import {
  CheckOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  InputNumber,
  Modal,
  Radio,
  Switch,
  TimePicker,
  Tooltip,
} from 'antd';
import classNames from 'classnames';
import { useContext, useEffect, useState } from 'react';

import { AlertModal } from 'app/components/commons/AlertModal/AlertModal';
import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import {
  AvailablityRestrictions,
  EARLY_CHECKIN,
  LATE_CHECKOUT,
} from 'app/components/commons/Experience/Availability/utils';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { TaskCenterContext } from 'app/context/TaskCenterContext/TaskCenterContext';
import { useExperienceAddOn } from 'app/hooks/data/core/extranet/experience/useExperienceAddOn';
import { DAY_OF_WEEK } from 'app/typings/core/admin/enum/dayOfWeek';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import { PRICE_DIFFERENTIATION_MODE } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import type {
  DayOfWeekStockConfiguration,
  StockConfigurationType,
} from 'app/typings/core/common/openingConfiguration/stockConfigurationType';
import { format } from 'app/utils/dayjs';
import { capitalizeFirstLetter } from 'app/utils/string';

import type { Task } from '../../TaskCenter/Modern/Types';
import { TaskName } from '../../TaskCenter/Modern/Types';

import styles from './ModernExtraAddOn.module.scss';

const RULES = [{ required: true, message: 'Required' }];
const MAX_QUANTITY = 1;

type FormAddOn = {
  time?: string;
  price?: number;
  closedDays?: number;
  availableDaysRadio?: AvailablityRestrictions;
  availableDays?: string[];
};

type ExtraAddOnType = {
  task: Task;
  isOpen?: boolean;
  onCompleteTask?: () => void;
};

export const ModernExtraAddOn = ({
  task,
  isOpen,
  onCompleteTask,
}: ExtraAddOnType) => {
  const [form] = Form.useForm();
  const formValues: FormAddOn = Form.useWatch([], form);

  const { hotelId } = useContext(HotelContext);
  const { clearOpenTask } = useContext(TaskCenterContext);
  const { currencyInput } = useContext(CurrencyContext);

  const { mutateAsync: saveAddOn } = useExperienceAddOn(hotelId);

  const isSpecificDay =
    Form.useWatch('availableDaysRadio', form) ===
    AvailablityRestrictions.SpecificDays;

  const [isSwitchOn, setIsSwitchOn] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [isSuccessfullySave, setIsSuccessfullySave] = useState(false);
  const [alertClose, setAlertClose] = useState(true);

  const onFinish = async (values: FormAddOn) => {
    const selectedDays = form.getFieldValue('availableDays') || [];
    const isDaily =
      values.availableDaysRadio === AvailablityRestrictions.SpecificDays;

    const stockConfiguration: StockConfigurationType = isDaily
      ? {
          type: OPENING_CONFIGURATION_TYPE.DAILY,
          days: Object.values(DAY_OF_WEEK).reduce(
            (acc, day) => ({
              ...acc,
              [day]: {
                stock: selectedDays.includes(day) ? null : 0,
                isClosed: !selectedDays.includes(day),
              },
            }),
            {} as DayOfWeekStockConfiguration
          ),
        }
      : {
          type: OPENING_CONFIGURATION_TYPE.CONSTANT,
          configuration: { stock: null },
        };

    const priceConfiguration = {
      type: OPENING_CONFIGURATION_TYPE.CONSTANT,
      configuration: { price: values.price },
      priceDifferentiationMode: PRICE_DIFFERENTIATION_MODE.NONE,
    };

    const formattedValues = {
      name: task.taskName,
      openingConfiguration: { stockConfiguration, priceConfiguration },
      categoryId:
        task.taskName === TaskName.EarlyCheckin ? EARLY_CHECKIN : LATE_CHECKOUT,
      time: (values.time && format(new Date(values.time), 'HH:mm')) ?? '',
      closedDays: isSwitchOn ? values.closedDays : null,
      maxQuantity: MAX_QUANTITY,
    };

    await saveAddOn(formattedValues);

    onCompleteTask?.();
    clearOpenTask();
    setIsSuccessfullySave(true);
    setAlertClose(false);
  };

  const handleClose = () => {
    clearOpenTask();
  };

  const handleSwitchChange = (day: string, checked: boolean) => {
    const selectedDays = form.getFieldValue('availableDays') || [];
    const newSelectedDays = checked
      ? [...selectedDays, day]
      : selectedDays.filter((d: string) => d !== day);

    form.setFieldsValue({ availableDays: newSelectedDays });
  };

  useEffect(() => {
    if (
      form.isFieldsTouched() &&
      formValues.time &&
      form.getFieldsError().every((item) => item.errors.length === 0)
    ) {
      setCanSubmit(true);
    } else {
      setCanSubmit(false);
    }
  }, [form, setCanSubmit, formValues]);

  const onCloseValidation = () => {
    setAlertClose(true);
    setIsSuccessfullySave(false);
  };

  if (isSuccessfullySave && !alertClose) {
    return (
      <AlertModal
        type="success"
        icon={<CheckOutlined />}
        message="Request successfully sent!"
        description="Your Account Manager will now review and publish your Add-on."
        closable
        onClose={onCloseValidation}
        isOpen={!alertClose}
      />
    );
  }

  return (
    <Form form={form} onFinish={onFinish} preserve={false}>
      <Modal
        width={684}
        className={styles.extraAddOnModal}
        open={isOpen}
        title={
          <div className={styles.header}>
            <h1>{task.title}</h1>
            <p> {task.description} </p>
          </div>
        }
        onCancel={handleClose}
        wrapClassName={styles.extraAddOnModalContainer}
        centered
        footer={[
          <Button size="large" type="ghost" onClick={handleClose}>
            Close
          </Button>,
          <Button
            key="save"
            size="large"
            type="primary"
            htmlType="submit"
            onClick={form.submit}
            className="saveBtn"
            disabled={!canSubmit}
          >
            Save
          </Button>,
        ]}
        destroyOnClose
      >
        <div className={styles.body}>
          <Alert
            type="info"
            message={`Your optional ${task.taskName.toLocaleLowerCase()} will be set up for all your packages`}
            icon={<InfoCircleOutlined />}
            showIcon
          />
          <div className={classNames([styles.bodySection, styles.timeCost])}>
            <div className={styles.time}>
              <div className={styles.title}>
                Time of {task.taskName.toLocaleLowerCase()}
              </div>
              <Form.Item name="time" rules={RULES}>
                <TimePicker minuteStep={30} showSecond={false} format="HH:mm" />
              </Form.Item>
            </div>
            <div className={styles.cost}>
              <div className={styles.title}>
                Extra cost
                <Tooltip
                  title={`Add the public price of your ${task.taskName}, when booked on site.`}
                  className={styles.extraCostTooltip}
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </div>
              <div className={styles.input}>
                <Form.Item style={{ width: '100%' }} name="price" rules={RULES}>
                  {currencyInput({
                    min: 0,
                    onWheel: (e) => e.currentTarget.blur(),
                  })}
                </Form.Item>
              </div>
            </div>
          </div>
          <div
            className={classNames([styles.bodySection, styles.extraSection])}
          >
            <div className={styles.titleSubtitle}>
              <div className={styles.title}>Available days</div>
              <div className={styles.subTitle}>
                Configure days when the amenity is available for guests
              </div>
            </div>

            <div>
              <Form.Item name="availableDaysRadio" noStyle rules={RULES}>
                <Radio.Group className={styles.availableDaysRadios}>
                  <Radio value={AvailablityRestrictions.EveryDay}>
                    Every day
                  </Radio>
                  <Radio value={AvailablityRestrictions.SpecificDays}>
                    Only on specific days
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </div>
            {isSpecificDay && (
              <div className={styles.selectDaysAvailable}>
                <Form.Item name="availableDays" noStyle>
                  {Object.values(DAY_OF_WEEK).map((day) => {
                    const selectedDays =
                      form.getFieldValue('availableDays') || [];

                    return (
                      <div key={day} className={styles.weekdaySwitch}>
                        <Switch
                          checked={selectedDays.includes(day)}
                          onChange={(checked) =>
                            handleSwitchChange(day, checked)
                          }
                        />
                        <span className={styles.day}>
                          {capitalizeFirstLetter(day)}
                        </span>
                      </div>
                    );
                  })}
                </Form.Item>
              </div>
            )}
          </div>
          <div
            className={classNames(styles.bodySection, styles.extraSection, {
              [styles.disabled]: !isSwitchOn,
            })}
          >
            <div className={styles.sectionTitle}>
              <div className={styles.titleSubtitle}>
                <div className={styles.title}>Last minute restriction</div>
                <div className={styles.subTitle}>
                  Set a rule to remove last minute availability of your{' '}
                  {task.taskName} add-on
                </div>
              </div>
              <Form.Item>
                <Switch
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  onChange={setIsSwitchOn}
                  checked={isSwitchOn}
                />
              </Form.Item>
            </div>

            <div className={styles.sectionText}>
              <span>Remove availability</span>
              <div className={styles.input}>
                <Form.Item
                  name="closedDays"
                  rules={isSwitchOn ? RULES : undefined}
                >
                  <InputNumber
                    disabled={!isSwitchOn}
                    onWheel={(e) => e.currentTarget.blur()}
                    min={0}
                  />
                </Form.Item>
              </div>
              <span>days before check-in</span>
            </div>
          </div>
        </div>
      </Modal>
    </Form>
  );
};
